import React from 'react';
import { useTranslation } from 'react-i18next';
import { useResetPassword } from '../../hooks/useResetPassword';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { FormErrorBanner } from '../../components/shared/FormErrorBanner';
import { PasswordRequirements } from '../../components/shared/PasswordRequirements';

export const ResetPasswordPage: React.FC = () => {
  const { t } = useTranslation();
  const { password, setPassword, passwordConfirmation, setPasswordConfirmation, fieldErrors, formErrors, handleSubmit, loading, success } = useResetPassword();

  const inputClass = "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50";
  const labelClass = "text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 mb-2 block";

  const renderFieldError = (errors: string[]) => {
    if (errors.length === 0) {
      return null;
    }

    return <p className="mt-2 text-sm text-destructive">{t(errors[0])}</p>;
  };

  return (
    <div className="max-w-md mx-auto p-6 mt-10">
      <Card>
        <CardHeader>
          <h3 className="text-2xl leading-none tracking-tight">{t('auth.reset_password.title')}</h3>
          <p className="text-sm text-muted-foreground">{t('auth.reset_password.subtitle')}</p>
        </CardHeader>
        <CardContent>
          <FormErrorBanner errors={formErrors} className="mb-4" />

          {success ? (
            <div className="bg-green-50 text-green-900 px-4 py-3 rounded-md text-sm">
              {t('auth.reset_password.success')}
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4" noValidate>
              <div>
                <label className={labelClass}>{t('auth.reset_password.password_label')}</label>
                <input
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  className={inputClass}
                  placeholder={t('auth.reset_password.password_placeholder')}
                  required
                />
                <div className="mt-2">
                  <PasswordRequirements password={password} />
                </div>
                {renderFieldError(fieldErrors.password)}
              </div>

              <div>
                <label className={labelClass}>{t('auth.reset_password.password_confirmation_label')}</label>
                <input
                  type="password"
                  value={passwordConfirmation}
                  onChange={(event) => setPasswordConfirmation(event.target.value)}
                  className={inputClass}
                  placeholder={t('auth.reset_password.password_confirmation_placeholder')}
                  required
                />
                {renderFieldError(fieldErrors.passwordConfirmation)}
              </div>

              <Button type="submit" disabled={loading} className="w-full">
                {loading ? t('common.loading') : t('auth.reset_password.submit')}
              </Button>
            </form>
          )}

          <div className="text-center text-sm mt-4">
            <a href="/login" className="text-foreground underline hover:no-underline font-medium">
              {t('auth.reset_password.back_to_login')}
            </a>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
